// External Dependencies:
import { DateTime } from 'luxon'

// Internal Dependencies:
import { getUtcTimestampSeconds } from 'utilities/time'
import { getStorage, setStorage } from 'utilities/storage'

let timezoneType = siteData.timezone_type,
	userTimezoneOffset = new Date().getTimezoneOffset(),
	stationTimezone = 'Australia/Sydney',
	stationTimezoneOffset = 0,
	timezone,
	timezoneOffset,
	rewindMaxHours = siteData.rewind_max_hours,
	allowFutureFastForward = siteData.allow_future_fast_forward,
	futureFastForwardMaxHours = siteData.future_fast_forward_max_hours

if ( 'user' == timezoneType || 'local' == timezoneType ) {
	timezone = 'local' // Used by the datetime library to get the user timezone.
	timezoneOffset = userTimezoneOffset
} else {
	timezone = stationTimezone
	timezoneOffset = stationTimezoneOffset
}

const localStore = {
	'siteId': getStorage( 'site_id' ),
	'userLocation': getStorage( 'user_location' )
}

let siteId = localStore['siteId'] ? localStore['siteId'] : siteData.site_id
let userLocation

if ( localStore['userLocation'] ) {
	userLocation = JSON.parse( localStore['userLocation'] )
} else {
	userLocation = siteData.user_location
}

const initialState = {
	isLoading: false,
	isBuffering: false,
	isPlaying: false,
	isLive: true,
	isSeeking: false,
	isFadingOut: false,
	isFullVolume: true,
	mediaError: false,
	canFastForward: false,
	canGoToNext: false,
	showRewindWarning: false,
	seekToDirection: '',
	adjustedSeek: 0, // Seconds of adjustment from rewind/fast forward.
	seekSeconds: 0, // The actual seek value from the beginning of a track, in seconds.
	seekSecondsForPercentCalcOnly: 0, // Updated every second.
	seekPercent: '',
	siteId: siteId,
	userLocation: userLocation,
	timezoneType: timezoneType,
	userTimezoneOffset: userTimezoneOffset,
	stationTimezoneOffset: stationTimezoneOffset,
	timezone: timezone,
	timezoneOffset: timezoneOffset,
	playlistTimezone: stationTimezone,
	rewindMaxHours: rewindMaxHours,
	allowFutureFastForward: allowFutureFastForward || false,
	futureFastForwardMaxHours: futureFastForwardMaxHours,
	maxPages: 1,
	byId: {},
	nowPlaying: false,
	nowPlayingLive: false,
	playlist: false,
	playlistCurrentIndex: 0,
	playlistProgramData: 0,
	playerOffset: 0,
	hasInitialPlayerOffset: false,
	secondsFromTrackStartToNow: 0, // Seconds. Used when navigating to previous tracks.
	upNext: [],
	fullSchedule: false,
	fullScheduleDaysReceived: [],
	placesSearchResults: [],
	stationsById: {},
	stationsCloseToLocation: {},
	selectedItem: false
}

function toRad( x ) {
	return x * Math.PI / 180
}

function haversineDistance( lat1, lng1, lat2, lng2 ) {
	let earthRadius = 6371, // KM.
		latDifference = lat2 - lat1,
		latDifferenceRad = toRad( latDifference ),
		lngDifference = lng2 - lng1,
		lngDifferenceRad = toRad( lngDifference )

	let a = Math.sin( latDifferenceRad / 2 ) * Math.sin( latDifferenceRad / 2 ) +
		Math.cos( toRad( lat1 ) ) * Math.cos( toRad( lat2 ) ) *
		Math.sin( lngDifferenceRad / 2 ) * Math.sin( lngDifferenceRad / 2 )

	let c = 2 * Math.atan2( Math.sqrt( a ), Math.sqrt( 1 - a ) )
	let distance = earthRadius * c

	return distance
}

function getStationsCloseToLocation( stationsById, lat, lng ) {
	let stationsCloseToLocation = {},
		matchingIds

	if ( ! stationsById || ! lat || ! lng ) {
		return stationsCloseToLocation
	}

	// Convert to array (to sort), adding distance:
	let stationsArray = Object.keys( stationsById ).map( ( key ) => {
		let station = stationsById[ key ]
		let distance = haversineDistance( lat, lng, station.lat, station.lng )

		return {
			...station,
			id: key,
			'distance': distance
		}
	})

	// Sort by distance from user location:
	stationsArray.sort( ( a, b ) => {
		if ( a.distance < b.distance ) {
			return -1
		} else if ( a.distance > b.distance ) {
			return 1
		}

		return 0
	} )

	// Limit results number:
	stationsArray = stationsArray.slice( 0, 3 )

	return stationsArray
}

function getSeekedToPointInTime( state, utcTimestampSeconds ) {
	// Below using previous value of state.secondsFromTrackStartToNow (1 second in the past).

	// Is it not live (rewinded), with the playing track the same as the live track?
	if ( ! state.isLive && state.nowPlaying.start_timestamp_gmt === state.nowPlayingLive.start_timestamp_gmt ) {
		// Set the seeked-to point in time:
		utcTimestampSeconds = state.nowPlaying.start_timestamp_gmt + state.seekSecondsForPercentCalcOnly
	} else if ( ! state.isLive ) {
		// Set the seeked-to point in time:
		utcTimestampSeconds = utcTimestampSeconds - state.secondsFromTrackStartToNow + state.seekSecondsForPercentCalcOnly
	}

	return utcTimestampSeconds
}

function getSeekData( state ) {
	let nowPlaying = state.nowPlaying,
		utcTimestampSeconds = getUtcTimestampSeconds(),
		duration = nowPlaying.end_timestamp_gmt - nowPlaying.start_timestamp_gmt,
		secondsFromTrackStartToNow = utcTimestampSeconds - nowPlaying.start_timestamp_gmt

	let seekedToPointInTime = getSeekedToPointInTime( state, utcTimestampSeconds )

	let seekSeconds = seekedToPointInTime - nowPlaying.start_timestamp_gmt
	let seekPercent = seekSeconds / duration

	return {
		seekSeconds,
		seekPercent,
		secondsFromTrackStartToNow
	}
}

export default function reducer( state = initialState, action ) {
	const { payload } = action
	let newState,
		nowPlaying,
		nowPlayingLive,
		nowPlayingNext,
		playlistCurrentIndex,
		newIndex,
		playlistNext,
		isLive,
		fullSchedule,
		fullScheduleDaysReceived,
		now,
		utcTimestamp,
		utcTimestampSeconds,
		playerOffset,
		secondsFromTrackStartToNow,
		secondsFromTrackEndToNow,
		secondsFromNowToTrackStart,
		offsetHours,
		seekData,
		adjustedSeek,
		seekSeconds,
		seekSecondsForPercentCalcOnly,
		seekPercent,
		seekedToPointInTime,
		duration,
		isFadingOut,
		isFullVolume,
		todayKey,
		programsToday,
		programs,
		keys,
		canFastForward,
		canGoToNext,
		showRewindWarning,
		mediaError,
		timezone,
		timezoneOffset,
		stationsCloseToLocation,
		futureFastForwardMaxHours

	switch ( action.type ) {
		case 'TOGGLE_PLAYING':
			isLive = state.isLive
			seekSeconds = state.seekSeconds
			seekPercent = state.seekPercent
			secondsFromTrackStartToNow = state.secondsFromTrackStartToNow
			playerOffset = state.playerOffset
			let hasInitialPlayerOffset = state.hasInitialPlayerOffset

			if ( ! hasInitialPlayerOffset ) {
				hasInitialPlayerOffset = true

				if ( state.isLive && state.nowPlaying && state.nowPlaying.start_timestamp_gmt ) {
					// Get the offset between the program start time and now:

					// Get the GMT timestamp:
					utcTimestampSeconds = getUtcTimestampSeconds()

					playerOffset = utcTimestampSeconds - state.nowPlaying.start_timestamp_gmt

					// Add the program offset:
					if ( state.nowPlaying.start_time_offset ) {
						playerOffset += state.nowPlaying.start_time_offset
					}
				} else if ( state.nowPlaying && state.nowPlaying.start_time_offset ) {
					// Program offset:
					playerOffset = state.nowPlaying.start_time_offset
				} else {
					playerOffset = 0
				}
			}

			// Was previously playing (now paused):
			if ( state.isPlaying ) {
				isLive = false

				if ( ! state.seekSeconds || 0 === state.seekSeconds ) {
					seekData = getSeekData( state )
					seekSeconds = seekData.seekSeconds
					seekPercent = seekData.seekPercent
				}
			}

			return {
				...state,
				isPlaying: ! state.isPlaying,
				playerOffset: playerOffset,
				mediaError: false,
				isLive: isLive,
				seekSeconds: seekSeconds,
				seekSecondsForPercentCalcOnly: seekSeconds,
				seekPercent: seekPercent,
				hasInitialPlayerOffset: hasInitialPlayerOffset
			}

		case 'SET_IS_PLAYING':
			return {
				...state,
				isPlaying: payload
			}

		case 'SET_CUSTOM_NOW_PLAYING':
			now = getUtcTimestampSeconds()
			nowPlaying = {
				key: 'custom',
				is_program: true,
				is_livestream: false,
				livestream_url: '',
				is_partial_overlap: false,
				title: '',
				audio_file_url: '',
				start_timestamp_gmt: now,
				end_timestamp_gmt: now + payload.data.seconds,
				start_time_offset: 0,
				guests: '',
				series: '',
				series_slug: '',
				date: '',
				...payload.data
			}

			return {
				...state,
				nowPlaying: nowPlaying,
				playlist: payload.extraData.playlist,
				playlistCurrentIndex: payload.extraData.playlistCurrentIndex,
				playlistProgramData: payload.extraData.playlistProgramData,
				adjustedSeek: 0,
				seekSeconds: 0,
				seekSecondsForPercentCalcOnly: 0,
				seekPercent: 0,
				seekToDirection: '',
				isLive: false,
				canFastForward: true,
				canGoToNext: true,
				mediaError: false
			}

		case 'GET_NOW_PLAYING':
			nowPlaying = state.nowPlaying
			nowPlayingLive = state.nowPlayingLive
			isLive = state.isLive
			canFastForward = state.canFastForward
			mediaError = state.mediaError
			isFadingOut = state.isFadingOut
			isFullVolume = state.isFullVolume

			if ( nowPlaying.is_partial_overlap ) {
				seekedToPointInTime = getSeekedToPointInTime( state, payload.timestamp )
			}

			// Get live program data:
			if ( state.nowPlaying.is_playlist && state.playlist ) {
				newIndex = state.playlistCurrentIndex - 1

				if ( state.playlist[ newIndex ] ) {
					playlistNext = state.playlist[ newIndex ]
					now = getUtcTimestampSeconds()
					nowPlaying = {
						key: 'custom',
						is_program: true,
						is_livestream: false,
						livestream_url: '',
						is_partial_overlap: false,
						title: '',
						audio_file_url: '',
						start_timestamp_gmt: now,
						end_timestamp_gmt: now + playlistNext.seconds,
						start_time_offset: 0,
						guests: '',
						series: '',
						series_slug: '',
						date: '',
						is_favourites: state.nowPlaying.is_favourites,
						is_recent_episodes: state.nowPlaying.is_recent_episodes,
						is_podcasts: state.nowPlaying.is_podcasts,
						...playlistNext
					}
					playlistCurrentIndex = newIndex
				}
			} else if ( state.fullSchedule ) {
				programs = state.fullSchedule

				// Sort oldest to newest:
				keys = Object.keys( programs ).sort( ( a, b ) => {
					return programs[ a ].start_timestamp_gmt - programs[ b ].start_timestamp_gmt
				} )

				let hasLive = false,
					hasNext = ! nowPlaying.is_partial_overlap // Only check for next if overlap.

				keys.some( ( key, i ) => {
					if ( ! hasLive && programs[ key ].start_timestamp_gmt > payload.timestamp ) {
						let liveKey = keys[ i - 1 ]
						nowPlayingLive = {
							...programs[ liveKey ]
						}

						hasLive = true
					}

					// Check for changes to overlapped tracks:
					if ( seekedToPointInTime ) {
						if ( ! hasNext && programs[ key ].start_timestamp_gmt > seekedToPointInTime ) {
							let seekedToKey = keys[ i - 1 ]
							nowPlayingNext = programs[ key ]

							nowPlaying = {
								...programs[ seekedToKey ]
							}

							hasNext = true
						}
					}

					if ( hasLive && hasNext ) {
						return true
					}
				})
			}

			if ( state.nowPlaying.is_partial_overlap ) {
				// Has the track changed?
				if ( nowPlaying.start_timestamp_gmt != state.nowPlaying.start_timestamp_gmt ) {
					isFadingOut = false
					isFullVolume = true
				} else if (
					nowPlayingNext &&
					seekedToPointInTime < ( nowPlayingNext.start_timestamp_gmt ) &&
					seekedToPointInTime >= ( nowPlayingNext.start_timestamp_gmt - 4 )
				) {
					// Fade out 4 seconds before:
					isFadingOut = true
					isFullVolume = false
				}
			}

			// Is the live track different to the current track?
			if ( nowPlayingLive.start_timestamp_gmt != nowPlaying.start_timestamp_gmt ) {
				if ( isLive ) {
					// Reset the media error value.
					mediaError = false
				}
			}

			if ( isLive ) {
				nowPlaying = nowPlayingLive
			}

			// Is this track a live stream?
			if ( nowPlaying.is_livestream && nowPlaying.livestream_url ) {
				// Is it actually live now?
				utcTimestampSeconds = getUtcTimestampSeconds()

				if ( utcTimestampSeconds > nowPlaying.start_timestamp_gmt && utcTimestampSeconds < nowPlaying.end_timestamp_gmt ) {
					nowPlaying.audio_file_url = nowPlaying.livestream_url
					isLive = true
					canFastForward = false
				}
			}

			return {
				...state,
				nowPlayingLive: nowPlayingLive,
				nowPlaying: nowPlaying,
				isLive: isLive,
				canFastForward: canFastForward,
				mediaError: mediaError,
				isFadingOut: isFadingOut,
				isFullVolume: isFullVolume
			}

		case 'GO_TO_PREVIOUS_TRACK':
			nowPlaying = state.nowPlaying
			showRewindWarning = state.showRewindWarning
			playerOffset = 0
			mediaError = false

			// Get live program data:
			if ( state.nowPlaying.is_playlist && state.playlist ) {
				newIndex = state.playlistCurrentIndex + 1

				if ( state.playlist[ newIndex ] ) {
					playlistNext = state.playlist[ newIndex ]
					now = getUtcTimestampSeconds()
					nowPlaying = {
						key: 'custom',
						is_program: true,
						is_livestream: false,
						livestream_url: '',
						is_partial_overlap: false,
						title: '',
						audio_file_url: '',
						start_timestamp_gmt: now,
						end_timestamp_gmt: now + playlistNext.seconds,
						start_time_offset: 0,
						guests: '',
						series: '',
						series_slug: '',
						date: '',
						is_favourites: state.nowPlaying.is_favourites,
						is_recent_episodes: state.nowPlaying.is_recent_episodes,
						is_podcasts: state.nowPlaying.is_podcasts,
						...playlistNext
					}
					playlistCurrentIndex = newIndex
				}
			} else if ( state.fullSchedule ) {
				programs = state.fullSchedule

				// Sort newest to oldest:
				keys = Object.keys( programs ).sort( ( a, b ) => {
					return programs[ b ].start_timestamp_gmt - programs[ a ].start_timestamp_gmt
				} )

				keys.some( ( key, i ) => {
					// Get the current program:
					if ( programs[ key ].start_timestamp_gmt < nowPlaying.start_timestamp_gmt ) {
						// Skip if it's not a program, try the next:
						if ( ! programs[ key ].is_program ) {
							return false
						}

						// Get the offset:
						if ( programs[ key ].start_time_offset ) {
							playerOffset = programs[ key ].start_time_offset
						}

						nowPlaying = {
							...programs[ key ]
						}

						return true
					}
				})
			}

			// Restrict rewind to a set number of hours in the past.
			// Get the offset between the program start time and now:
			utcTimestampSeconds = getUtcTimestampSeconds()
			secondsFromTrackStartToNow = utcTimestampSeconds - nowPlaying.start_timestamp_gmt
			offsetHours = secondsFromTrackStartToNow / 60 / 60

			if ( offsetHours >= state.rewindMaxHours ) {
				nowPlaying = state.nowPlaying
				showRewindWarning = true
				secondsFromTrackStartToNow = 0
			}

			// Is the new track the same as the previous track?
			if ( nowPlaying.audio_file_url == state.nowPlaying.audio_file_url ) {
				// If there is already a media error set, maintain that error state, as the file hasn't changed.
				if ( state.mediaError ) {
					mediaError = true
				}
			}

			return {
				...state,
				nowPlaying: nowPlaying,
				playlistCurrentIndex: playlistCurrentIndex,
				playerOffset: playerOffset,
				secondsFromTrackStartToNow: secondsFromTrackStartToNow,
				adjustedSeek: 0,
				seekSeconds: 0,
				seekSecondsForPercentCalcOnly: 0,
				seekPercent: 0,
				seekToDirection: '',
				isLive: false,
				showRewindWarning: showRewindWarning,
				mediaError: mediaError
			}

		case 'GO_TO_NEXT_TRACK':
			nowPlaying = state.nowPlaying
			playlistCurrentIndex = state.playlistCurrentIndex
			canFastForward = state.canFastForward
			canGoToNext = state.canGoToNext
			playerOffset = 0
			secondsFromTrackStartToNow = state.secondsFromTrackStartToNow
			isLive = false
			mediaError = false

			// Get live program data:
			if ( state.nowPlaying.is_playlist && state.playlist ) {
				newIndex = state.playlistCurrentIndex - 1

				if ( newIndex < 0 ) {
					newIndex = 0
				}

				if ( state.playlist[ newIndex ] ) {
					playlistNext = state.playlist[ newIndex ]
					now = getUtcTimestampSeconds()
					nowPlaying = {
						key: 'custom',
						is_program: true,
						is_livestream: false,
						livestream_url: '',
						is_partial_overlap: false,
						title: '',
						audio_file_url: '',
						start_timestamp_gmt: now,
						end_timestamp_gmt: now + playlistNext.seconds,
						start_time_offset: 0,
						guests: '',
						series: '',
						series_slug: '',
						date: '',
						is_favourites: state.nowPlaying.is_favourites,
						is_recent_episodes: state.nowPlaying.is_recent_episodes,
						is_podcasts: state.nowPlaying.is_podcasts,
						...playlistNext
					}
					playlistCurrentIndex = newIndex
				}
			} else if ( state.fullSchedule ) {
				programs = state.fullSchedule

				// Sort oldest to newest:
				keys = Object.keys( programs ).sort( ( a, b ) => {
					return programs[ a ].start_timestamp_gmt - programs[ b ].start_timestamp_gmt
				} )

				keys.some( ( key, i ) => {
					// Get the current program:
					if ( programs[ key ].start_timestamp_gmt > nowPlaying.start_timestamp_gmt ) {
						// Skip if it's not a program, try the next:
						if ( ! payload.isAutoNext && ! programs[ key ].is_program ) {
							return false
						}

						// Get the offset:
						if ( programs[ key ].start_time_offset ) {
							playerOffset = programs[ key ].start_time_offset
						}

						nowPlaying = {
							...programs[ key ]
						}

						return true
					}
				})
			}

			// Is the new track the same as the live track?
			if ( nowPlaying.start_timestamp_gmt == state.nowPlayingLive.start_timestamp_gmt ) {

			} else {
				// Get the offset between the program start time and now:
				utcTimestampSeconds = getUtcTimestampSeconds()
				secondsFromTrackStartToNow = utcTimestampSeconds - nowPlaying.start_timestamp_gmt
			}

			// Is this track a live stream?
			if ( nowPlaying.is_livestream && nowPlaying.livestream_url ) {
				// Is it actually live now?
				utcTimestampSeconds = getUtcTimestampSeconds()

				if ( utcTimestampSeconds > nowPlaying.start_timestamp_gmt && utcTimestampSeconds < nowPlaying.end_timestamp_gmt ) {
					nowPlaying.audio_file_url = nowPlaying.livestream_url
					isLive = true
					canFastForward = false
				}
			}

			// Is the new track the same as the previous track?
			if ( nowPlaying.audio_file_url == state.nowPlaying.audio_file_url ) {
				// If there is already a media error set, maintain that error state, as the file hasn't changed.
				if ( state.mediaError ) {
					mediaError = true
				}
			}

			// Restrict future fast forward.
			if ( state.canGoToNext && 'custom' != nowPlaying.key ) {
				// Get the offset between the program end time and now:
				secondsFromNowToTrackStart = nowPlaying.start_timestamp_gmt - utcTimestampSeconds
				offsetHours = secondsFromNowToTrackStart / 60 / 60

				if ( offsetHours >= state.futureFastForwardMaxHours ) {
					return {
						...state,
						canGoToNext: false
					}
				}
			}

			if ( 'custom' == nowPlaying.key ) {
				canFastForward = true
				canGoToNext = true
			}

			return {
				...state,
				nowPlaying: nowPlaying,
				playlistCurrentIndex: playlistCurrentIndex,
				playerOffset: playerOffset,
				secondsFromTrackStartToNow: secondsFromTrackStartToNow,
				adjustedSeek: 0,
				seekSeconds: 0,
				seekSecondsForPercentCalcOnly: 0,
				seekPercent: 0,
				seekToDirection: '',
				isLive: isLive,
				canFastForward: canFastForward,
				canGoToNext: canGoToNext,
				mediaError: mediaError
			}

		case 'GO_TO_TRACK':
			nowPlaying = state.nowPlaying
			canFastForward = state.canFastForward
			canGoToNext = state.canGoToNext
			playerOffset = 0
			secondsFromTrackStartToNow = state.secondsFromTrackStartToNow
			isLive = state.isLive
			mediaError = false

			// Get live program data:
			if ( state.fullSchedule ) {
				programs = state.fullSchedule

				// Sort oldest to newest:
				keys = Object.keys( programs ).sort( ( a, b ) => {
					return programs[ a ].start_timestamp_gmt - programs[ b ].start_timestamp_gmt
				} )

				keys.some( ( key, i ) => {
					// Get the new program:
					if ( key == payload ) {
						nowPlaying = {
							...programs[ key ]
						}

						// Get the offset:
						if ( programs[ key ].start_time_offset ) {
							playerOffset = programs[ key ].start_time_offset
						}

						return true
					}
				})
			}

			// Is this track a live stream?
			if ( nowPlaying.is_livestream && nowPlaying.livestream_url ) {
				// Is it actually live now?
				utcTimestampSeconds = getUtcTimestampSeconds()

				if ( utcTimestampSeconds > nowPlaying.start_timestamp_gmt && utcTimestampSeconds < nowPlaying.end_timestamp_gmt ) {
					nowPlaying.audio_file_url = nowPlaying.livestream_url
					isLive = true
					canFastForward = false
				}
			}

			// Is the new track the same as the live track?
			if ( nowPlaying.start_timestamp_gmt == state.nowPlayingLive.start_timestamp_gmt ) {

			} else {
				isLive = false

				// Get the offset between the program start time and now:
				utcTimestampSeconds = getUtcTimestampSeconds()
				secondsFromTrackStartToNow = utcTimestampSeconds - nowPlaying.start_timestamp_gmt
			}

			// Is the new track the same as the previous track?
			if ( nowPlaying.audio_file_url == state.nowPlaying.audio_file_url ) {
				// If there is already a media error set, maintain that error state, as the file hasn't changed.
				if ( state.mediaError ) {
					mediaError = true
				}
			}

			// Restrict future fast forward.
			if ( state.canFastForward ) {
				// Get the offset between the program end time and now:
				secondsFromNowToTrackStart = nowPlaying.start_timestamp_gmt - utcTimestampSeconds
				offsetHours = secondsFromNowToTrackStart / 60 / 60

				if ( offsetHours >= state.futureFastForwardMaxHours ) {
					return {
						...state,
						canGoToNext: false
					}
				}
			}

			return {
				...state,
				nowPlaying: nowPlaying,
				isPlaying: true,
				playerOffset: playerOffset,
				secondsFromTrackStartToNow: secondsFromTrackStartToNow,
				adjustedSeek: 0,
				seekSeconds: 0,
				seekSecondsForPercentCalcOnly: 0,
				seekPercent: 0,
				seekToDirection: '',
				isLive: isLive,
				canFastForward: canFastForward,
				canGoToNext: canGoToNext,
				mediaError: mediaError
			}

		case 'GO_TO_LIVE':
			// Get the offset between the program start time and now:

			// Get the GMT timestamp:
			utcTimestampSeconds = getUtcTimestampSeconds()

			playerOffset = utcTimestampSeconds - state.nowPlayingLive.start_timestamp_gmt

			// Add the program offset:
			if ( state.nowPlayingLive.start_time_offset ) {
				playerOffset += state.nowPlayingLive.start_time_offset
			}

			newState = {
				...state,
				nowPlaying: state.nowPlayingLive,
				isPlaying: true,
				playerOffset: playerOffset,
				secondsFromTrackStartToNow: 0,
				seekToDirection: '',
				isLive: true,
				mediaError: false
			}

			// Get seek:
			seekData = getSeekData( newState )
			seekSeconds = seekData.seekSeconds
			seekPercent = seekData.seekPercent

			return {
				...newState,
				seekSeconds: seekSeconds,
				seekSecondsForPercentCalcOnly: seekSeconds,
				seekPercent: seekPercent
			}

		case 'SHOW_REWIND_WARNING':
			return {
				...state,
				showRewindWarning: payload
			}

		case 'REWIND':
			seekSecondsForPercentCalcOnly = state.seekSecondsForPercentCalcOnly - 30

			if ( seekSecondsForPercentCalcOnly < 0 ) {
				seekSecondsForPercentCalcOnly = 0
			}

			if ( ! state.seekSeconds || 0 === state.seekSeconds ) {
				seekData = getSeekData( state )
				seekSeconds = seekData.seekSeconds
				seekPercent = seekData.seekPercent
			} else {
				seekSeconds = state.seekSeconds - 30
				seekPercent = state.seekPercent
			}

			return {
				...state,
				seekToDirection: 'rewind',
				seekSeconds: seekSeconds,
				seekSecondsForPercentCalcOnly: seekSecondsForPercentCalcOnly,
				seekPercent: seekPercent,
				isLive: false
			}

		case 'FAST_FORWARD':
			isLive = false
			seekSeconds = state.seekSeconds + 30
			seekSecondsForPercentCalcOnly = state.seekSecondsForPercentCalcOnly + 30
			utcTimestampSeconds = getUtcTimestampSeconds()

			// If playing the live track:
			if ( state.nowPlaying.start_timestamp_gmt == state.nowPlayingLive.start_timestamp_gmt ) {
				// Get the live offset:

				let playerOffset = utcTimestampSeconds - state.nowPlaying.start_timestamp_gmt

				// If past the live value,
				// set the value to move to live.
				if ( seekSeconds == playerOffset ) {
					isLive = true
				}
			}

			seekData = getSeekData( {
				...state,
				seekSecondsForPercentCalcOnly,
				isLive
			} )

			return {
				...state,
				seekToDirection: 'fastForward',
				seekSeconds: seekSeconds,
				seekSecondsForPercentCalcOnly: seekSecondsForPercentCalcOnly,
				seekPercent: seekData.seekPercent,
				isLive: isLive
			}

		case 'SEEK_TO':
			// payload.value = fraction (eg. 0.5).

			isLive = false
			duration = state.nowPlaying.end_timestamp_gmt - state.nowPlaying.start_timestamp_gmt
			seekSeconds = duration * payload.value

			return {
				...state,
				seekToDirection: 'seek',
				seekSeconds: seekSeconds,
				seekSecondsForPercentCalcOnly: seekSeconds,
				isLive: isLive
			}

		case 'SET_BUFFERING':
			return {
				...state,
				isBuffering: payload
			}

		case 'SET_MEDIA_ERROR':
			return {
				...state,
				mediaError: payload
			}

		case 'SET_SEEKING':
			return {
				...state,
				isSeeking: payload
			}

		case 'SET_SEEK_PERCENT':
			return {
				...state,
				seekPercent: payload
			}

		case 'CALCULATE_SEEK_PERCENT':
			seekData = getSeekData( state )
			seekSecondsForPercentCalcOnly = state.seekSecondsForPercentCalcOnly + 1
			seekSeconds = state.seekSeconds

			if ( ! state.seekSeconds || 0 === state.seekSeconds ) {
				seekSeconds = seekData.seekSeconds
			}

			// Get the offset between the requested seek time and now:
			utcTimestampSeconds = getUtcTimestampSeconds()
			canFastForward = true
			canGoToNext = true
			let secondsFromSeekTimeToNow = ( state.nowPlaying.start_timestamp_gmt + seekSecondsForPercentCalcOnly ) - utcTimestampSeconds
			let seekTimeOffsetHours = secondsFromSeekTimeToNow / 60 / 60

			if ( state.isLive ) {
				seekTimeOffsetHours = 0
			}

			if ( seekTimeOffsetHours >= state.futureFastForwardMaxHours && 'custom' != state.nowPlaying.key ) {
				canFastForward = false
				canGoToNext = false
			}

			return {
				...state,
				seekSeconds: seekSeconds,
				seekPercent: seekData.seekPercent,
				seekSecondsForPercentCalcOnly: seekSecondsForPercentCalcOnly,
				secondsFromTrackStartToNow: seekData.secondsFromTrackStartToNow,
				canFastForward: canFastForward,
				canGoToNext: canGoToNext
			}

		case 'REQUEST_SCHEDULE_DATA':
			return {
				...state,
				isLoading: true
			}

		case 'RECEIVE_FULL_SCHEDULE_DATA':
			fullSchedule = payload.data

			if ( state.fullSchedule ) {
				fullSchedule = Object.assign( {}, state.fullSchedule, payload.data )
			}

			// Add date to each:
			for ( let key in fullSchedule ) {
			    if ( ! fullSchedule.hasOwnProperty( key ) ) {
			        continue
			    }

				let date = DateTime.fromMillis(
					fullSchedule[ key ].start_timestamp_gmt * 1000,
					{ zone: state.timezone }
				).toFormat( 'MM-dd' )

			    fullSchedule[ key ].date = date
			}

			return {
				...state,
				fullSchedule: fullSchedule,
				fullScheduleDaysReceived: [
					...state.fullScheduleDaysReceived,
					`${payload.year}-${payload.month}-${payload.day}`
				]
			}

		case 'SET_CURRENT_POSITION':
			return {
				...state,
				userLatLng: {
					lat: payload.lat,
					lng: payload.lng
				}
			}

		case 'SET_LOCATION':
			stationsCloseToLocation = {}

			if ( payload.lat ) {
				stationsCloseToLocation = getStationsCloseToLocation( state.stationsById, payload.lat, payload.lng )
			}

			return {
				...state,
				userLocation: payload,
				stationsCloseToLocation: stationsCloseToLocation
			}

		case 'SET_SITE_ID':
			timezone = state.timezone
			fullSchedule = state.fullSchedule
			fullScheduleDaysReceived = state.fullScheduleDaysReceived

			if ( 'local' == state.timezoneType ) {
				timezone = payload.playlistTimezone
			}

			// Reset schedule data only if site id has changed.
			if ( payload.id != state.siteId ) {
				fullSchedule = false
				fullScheduleDaysReceived = []
			}

			return {
				...state,
				siteId: payload.id,
				playlistTimezone: payload.playlistTimezone,
				timezone: timezone,
				fullSchedule: fullSchedule,
				fullScheduleDaysReceived: fullScheduleDaysReceived
			}

		case 'SET_TIMEZONE_TYPE':
			if ( 'user' == payload ) {
				timezone = 'local'
				timezoneOffset = state.userTimezoneOffset
			} else {
				timezone = state.playlistTimezone
				timezoneOffset = state.stationTimezoneOffset
			}

			return {
				...state,
				timezoneType: payload,
				timezone: timezone,
				timezoneOffset: timezoneOffset
			}

		case 'RECEIVE_PLACES_SEARCH_RESULTS':
			return {
				...state,
				placesSearchResults: payload
			}

		case 'EMPTY_PLACES_SEARCH_RESULTS':
			return {
				...state,
				placesSearchResults: []
			}

		case 'RECEIVE_STATIONS_DATA':
			stationsCloseToLocation = {}

			if ( state.userLocation && state.userLocation.lat ) {
				stationsCloseToLocation = getStationsCloseToLocation( payload, state.userLocation.lat, state.userLocation.lng )
			}

			return {
				...state,
				stationsById: payload,
				stationsCloseToLocation: stationsCloseToLocation
			}

		case 'SET_SELECTED_ITEM':
			return {
				...state,
				selectedItem: payload
			}

		case 'SET_SCHEDULE_USER_DATA':
			// Logged in (start at 0 due to account badge awarded at account creation):
			rewindMaxHours = 0
			futureFastForwardMaxHours = 0
			canFastForward = true

			let badgesNumber = 0

			if ( payload.badges ) {
				badgesNumber = Object.keys( payload.badges ).length
				rewindMaxHours += badgesNumber * 24
				futureFastForwardMaxHours += badgesNumber * 2
			}

			if ( futureFastForwardMaxHours > 48 ) {
				futureFastForwardMaxHours = 48
			}

			// Get the offset between the program end time and now:
			utcTimestampSeconds = getUtcTimestampSeconds()
			secondsFromNowToTrackStart = state.nowPlaying.start_timestamp_gmt - utcTimestampSeconds
			offsetHours = secondsFromNowToTrackStart / 60 / 60

			if ( offsetHours >= futureFastForwardMaxHours ) {
				canFastForward = false
			}

			if ( payload.badges && payload.badges.unlimited_access ) {
				rewindMaxHours = 999999
				futureFastForwardMaxHours = 999999
				canFastForward = true
			}

			return {
				...state,
				rewindMaxHours: rewindMaxHours,
				futureFastForwardMaxHours: futureFastForwardMaxHours,
				canFastForward: canFastForward,
				allowFutureFastForward: canFastForward
			}
	}

	return state
}
